import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import api from "../api/api.js";
import { Eye, EyeOff } from "lucide-react";
import styles from "./Register.module.css";
import { registerSchema, getPasswordStrength } from "../lib/validations";
import { useToast } from "../Components/ui/Toast.jsx";

const PERSIST_KEY = "register_draft";

const Register = () => {
  const [showPassword, setShowPassword]   = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [isLoading, setIsLoading]         = useState(false);
  const [serverError, setServerError]     = useState("");
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Cargar borrador guardado
  const savedDraft = (() => {
    try { return JSON.parse(localStorage.getItem(PERSIST_KEY) || "{}"); }
    catch { return {}; }
  })();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(registerSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      documentType: "cedula",
      phoneCode: "+58",
      ...savedDraft,
    },
  });

  // Persistir datos del formulario en localStorage (excepto contraseñas)
  const watchedValues = watch();
  useEffect(() => {
    const { password, confirmPassword, ...safe } = watchedValues;
    localStorage.setItem(PERSIST_KEY, JSON.stringify(safe));
  }, [watchedValues]);

  // Observar contraseña para el indicador de fortaleza en tiempo real
  const passwordValue = watch("password", "");
  const strength = getPasswordStrength(passwordValue);

  const parseServerError = (err) => {
    const backendErrors = err.response?.data?.errors;
    if (backendErrors?.length) {
      return backendErrors.map(e => e.message).join(" · ");
    }
    const msg = err.response?.data?.error || err.response?.data?.message || "";
    // Mensajes más claros para errores de duplicado
    if (msg.toLowerCase().includes("email") || msg.toLowerCase().includes("correo")) {
      return "Ya existe una cuenta registrada con este correo electronico.";
    }
    if (msg.toLowerCase().includes("phone") || msg.toLowerCase().includes("telefono") || msg.toLowerCase().includes("number_phone")) {
      return "Ya existe una cuenta registrada con este numero de telefono.";
    }
    if (msg.toLowerCase().includes("duplicate") || msg.toLowerCase().includes("already") || msg.toLowerCase().includes("existe")) {
      return "Ya existe una cuenta con este correo o numero de telefono.";
    }
    return msg || "Error de servidor. Intentalo mas tarde.";
  };

  const onSubmit = async (data) => {
    setServerError("");
    setIsLoading(true);
    try {
      await api.post("/register", {
        name:              data.name.trim(),
        last_name:         data.last_name.trim(),
        email:             data.email,
        password:          data.password,
        birthdate:         data.birthdate,
        identification:    data.documentNumber,
        number_phone:      data.phoneCode + data.phoneNumber,
        emergency_contact: data.phoneCode + data.phoneNumber,
        authorization:     true,
      });

      localStorage.removeItem(PERSIST_KEY);
      showToast("Registro exitoso. Revisa tu correo para verificar tu cuenta.", { type: 'success' });
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setServerError(parseServerError(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Bloquear teclas no permitidas en campos de texto (solo letras y acentuados)
  const blockNonLetters = (e) => {
    if (e.key.length === 1 && !/[a-zA-ZÀ-ÿ\s]/.test(e.key)) {
      e.preventDefault();
    }
  };

  // Bloquear teclas no numéricas en campos de número
  const blockNonDigits = (e) => {
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!allowed.includes(e.key) && !/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className={styles.background}>
      <div className={styles.container}>
        <h2 className={styles.text}>Registro de Usuario</h2>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className={styles.formContainer}>
          <div className={styles.formGrid}>

            {/* ── COLUMNA 1 ── */}
            <div className={styles.column}>

              {/* NOMBRE */}
              <div className={styles.inputGroupTops}>
                <label htmlFor="name" className={styles.label}>Nombre</label>
                <input
                  id="name"
                  type="text"
                  placeholder="Tu nombre"
                  className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                  aria-invalid={errors.name ? 'true' : undefined}
                  onKeyDown={blockNonLetters}
                  {...register('name')}
                />
                {errors.name && (
                  <span id="name-error" className={styles.fieldError} role="alert">
                    {errors.name.message}
                  </span>
                )}
              </div>

              {/* APELLIDO */}
              <div className={styles.inputGroup}>
                <label htmlFor="last_name" className={styles.label}>Apellido</label>
                <input
                  id="last_name"
                  type="text"
                  placeholder="Tu apellido"
                  className={`${styles.input} ${errors.last_name ? styles.inputError : ''}`}
                  aria-describedby={errors.last_name ? 'last_name-error' : undefined}
                  aria-invalid={errors.last_name ? 'true' : undefined}
                  onKeyDown={blockNonLetters}
                  {...register('last_name')}
                />
                {errors.last_name && (
                  <span id="last_name-error" className={styles.fieldError} role="alert">
                    {errors.last_name.message}
                  </span>
                )}
              </div>

              {/* FECHA DE NACIMIENTO */}
              <div className={styles.inputGroup}>
                <label htmlFor="birthdate" className={styles.label}>Fecha de Nacimiento</label>
                <input
                  id="birthdate"
                  type="date"
                  max={new Date().toISOString().split('T')[0]}
                  className={`${styles.input} ${errors.birthdate ? styles.inputError : ''}`}
                  aria-describedby={errors.birthdate ? 'birthdate-error' : undefined}
                  aria-invalid={errors.birthdate ? 'true' : undefined}
                  {...register('birthdate')}
                />
                {errors.birthdate && (
                  <span id="birthdate-error" className={styles.fieldError} role="alert">
                    {errors.birthdate.message}
                  </span>
                )}
              </div>

              {/* TIPO DE DOCUMENTO */}
              <div className={styles.inputGroup}>
                <label htmlFor="documentType" className={styles.label}>Tipo de Documento</label>
                <select
                  id="documentType"
                  className={`${styles.select} ${errors.documentType ? styles.inputError : ''}`}
                  {...register('documentType')}
                >
                  <option value="cedula">Cédula</option>
                  <option value="pasaporte">Pasaporte</option>
                </select>
                {errors.documentType && (
                  <span className={styles.fieldError} role="alert">
                    {errors.documentType.message}
                  </span>
                )}
              </div>

              {/* NÚMERO DE DOCUMENTO */}
              <div className={styles.inputGroup}>
                <label htmlFor="documentNumber" className={styles.label}>Número de Documento</label>
                <input
                  id="documentNumber"
                  type="text"
                  inputMode="numeric"
                  placeholder="Ej: 12345678"
                  className={`${styles.input} ${errors.documentNumber ? styles.inputError : ''}`}
                  aria-describedby={errors.documentNumber ? 'documentNumber-error' : undefined}
                  aria-invalid={errors.documentNumber ? 'true' : undefined}
                  onKeyDown={blockNonDigits}
                  {...register('documentNumber')}
                />
                {errors.documentNumber && (
                  <span id="documentNumber-error" className={styles.fieldError} role="alert">
                    {errors.documentNumber.message}
                  </span>
                )}
              </div>
            </div>

            {/* ── COLUMNA 2 ── */}
            <div className={styles.column}>

              {/* EMAIL */}
              <div className={styles.inputGroupTops}>
                <label htmlFor="email" className={styles.label}>Correo Electrónico</label>
                <input
                  id="email"
                  type="email"
                  placeholder="correo@ejemplo.com"
                  className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  aria-invalid={errors.email ? 'true' : undefined}
                  {...register('email')}
                />
                {errors.email && (
                  <span id="email-error" className={styles.fieldError} role="alert">
                    {errors.email.message}
                  </span>
                )}
              </div>

              {/* CONFIRMAR EMAIL */}
              <div className={styles.inputGroup}>
                <label htmlFor="confirmEmail" className={styles.label}>Confirmar Correo</label>
                <input
                  id="confirmEmail"
                  type="email"
                  placeholder="Repite tu correo electrónico"
                  onPaste={(e) => e.preventDefault()} // Forzar escritura manual
                  className={`${styles.input} ${errors.confirmEmail ? styles.inputError : ''}`}
                  aria-describedby={errors.confirmEmail ? 'confirmEmail-error' : undefined}
                  aria-invalid={errors.confirmEmail ? 'true' : undefined}
                  {...register('confirmEmail')}
                />
                {errors.confirmEmail && (
                  <span id="confirmEmail-error" className={styles.fieldError} role="alert">
                    {errors.confirmEmail.message}
                  </span>
                )}
              </div>

              {/* CONTRASEÑA + INDICADOR DE FORTALEZA */}
              <div className={styles.inputGroup}>
                <label htmlFor="password" className={styles.label}>Contraseña</label>
                <div className={styles.inputWrapper}>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Mínimo 8 caracteres"
                    className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                    aria-describedby={errors.password ? 'password-error' : undefined}
                    aria-invalid={errors.password ? 'true' : undefined}
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className={styles.iconButton}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {/* Barra de fortaleza: aparece al empezar a escribir */}
                {passwordValue && (
                  <div className={styles.strengthContainer}>
                    <div className={styles.strengthBar}>
                      <div
                        className={`${styles.strengthFill} ${styles[`strength_${strength?.level}`]}`}
                        style={{ width: `${strength?.percent || 0}%` }}
                      />
                    </div>
                    <span className={`${styles.strengthLabel} ${styles[`strengthText_${strength?.level}`]}`}>
                      {strength?.label}
                    </span>
                  </div>
                )}

                {errors.password && (
                  <span id="password-error" className={styles.fieldError} role="alert">
                    {errors.password.message}
                  </span>
                )}
              </div>

              {/* CONFIRMAR CONTRASEÑA */}
              <div className={styles.inputGroup}>
                <label htmlFor="confirmPassword" className={styles.label}>Confirmar Contraseña</label>
                <div className={styles.inputWrapper}>
                  <input
                    id="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    placeholder="Repite tu contraseña"
                    onPaste={(e) => e.preventDefault()} // Forzar escritura manual
                    className={`${styles.input} ${errors.confirmPassword ? styles.inputError : ''}`}
                    aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                    aria-invalid={errors.confirmPassword ? 'true' : undefined}
                    {...register('confirmPassword')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(v => !v)}
                    className={styles.iconButton}
                    aria-label={showConfirm ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span id="confirmPassword-error" className={styles.fieldError} role="alert">
                    {errors.confirmPassword.message}
                  </span>
                )}
              </div>

              {/* TELÉFONO */}
              <div className={styles.inputGroup}>
                <label className={styles.label}>Teléfono</label>
                <div className={styles.phoneContainer}>
                  <select
                    className={`${styles.select} ${errors.phoneCode ? styles.inputError : ''}`}
                    {...register('phoneCode')}
                  >
                    <option value="+58">+58 Venezuela</option>
                    <option value="+57">+57 Colombia</option>
                    <option value="+1">+1 USA</option>
                  </select>
                  <div style={{ flex: 1 }}>
                    <input
                      id="phoneNumber"
                      type="text"
                      inputMode="numeric"
                      placeholder="Número de teléfono"
                      className={`${styles.input} ${errors.phoneNumber ? styles.inputError : ''}`}
                      aria-describedby={errors.phoneNumber ? 'phoneNumber-error' : undefined}
                      aria-invalid={errors.phoneNumber ? 'true' : undefined}
                      onKeyDown={blockNonDigits}
                      {...register('phoneNumber')}
                    />
                    {errors.phoneNumber && (
                      <span id="phoneNumber-error" className={styles.fieldError} role="alert">
                        {errors.phoneNumber.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error de servidor */}
          {serverError && <p className={styles.errorText}>{serverError}</p>}

          <div className={styles.submitContainer}>
            <button
              type="submit"
              className={styles.submit}
              disabled={isLoading}
            >
              {isLoading ? 'Registrando...' : 'Registrarse'}
            </button>
          </div>
        </form>

        <p className={styles.hasAccount}>
          ¿Ya tienes cuenta?{' '}
          <span className={styles.loginLink} onClick={() => navigate('/login')}>
            Inicia sesión aquí.
          </span>
        </p>
      </div>
    </div>
  );
};

export default Register;
